import {
  AllTournamentsPagination,
  CasualTournamentEntity,
  TournamentTagsEnum,
  TournamentsQueryParams,
} from '@fantaz/shared/data-access/models';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  TOURNAMENTS_FEATURE_KEY,
  TournamentsState,
  casualTournamentAdapter,
} from './tournaments.reducer';
import { shuffle } from '@fantaz/shared/utils/filters';

// Lookup the 'CasualTournament' feature state managed by NgRx
export const getTournamentsState = createFeatureSelector<TournamentsState>(
  TOURNAMENTS_FEATURE_KEY,
);

const { selectAll, selectEntities } = casualTournamentAdapter.getSelectors();

export const getTournamentsLoaded = createSelector(
  getTournamentsState,
  (state: TournamentsState) => state.loaded,
);

export const getTournamentsError = createSelector(
  getTournamentsState,
  (state: TournamentsState) => state.error,
);

export const getTournamentsErrorLoadingMore = createSelector(
  getTournamentsState,
  (state: TournamentsState) => state.errorLoadingMore,
);

export const getAllCasualTournament = createSelector(
  getTournamentsState,
  (state: TournamentsState): CasualTournamentEntity[] => selectAll(state),
);

export const getAllTournaments = createSelector(
  getTournamentsState,
  ({ cashTournaments, prizeTournaments }): CasualTournamentEntity[] => {
    let tournaments = prizeTournaments?.items || [];

    if (cashTournaments?.items?.length) {
      tournaments = [...tournaments, ...cashTournaments.items];
    }
    return tournaments;
  },
);

export const getCashTournaments = createSelector(
  getTournamentsState,
  (state: TournamentsState): CasualTournamentEntity[] =>
    state.cashTournaments?.items || [],
);

export const getPrizeTournaments = createSelector(
  getTournamentsState,
  (state: TournamentsState): CasualTournamentEntity[] =>
    state.prizeTournaments?.items || [],
);

export const getCasualTournamentEntities = createSelector(
  getTournamentsState,
  (state: TournamentsState) => selectEntities(state),
);

export const getTournamentParams = createSelector(
  getTournamentsState,
  (state: TournamentsState): TournamentsQueryParams => state.params,
);

export const getSelectedId = createSelector(
  getTournamentsState,
  (state: TournamentsState) => state.selectedId,
);

export const getSelected = createSelector(
  getCasualTournamentEntities,
  getSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
);

export const getFilterByGames = createSelector(
  getTournamentsState,
  (state: TournamentsState): string[] => state.filterByGames || [],
);

export const getFilterByTags = createSelector(
  getTournamentsState,
  (state: TournamentsState): string[] => state.filterByTags || [],
);

export const getSearchTerm = createSelector(
  getTournamentsState,
  (state: TournamentsState): string => state.searchTerm || '',
);

export const getFilterTags = createSelector(
  getTournamentsState,
  ({ eventsCountByTag }) => {
    const tagsOrder = Object.values(TournamentTagsEnum);

    return eventsCountByTag
      ?.map(
        ({ tag }) =>
          TournamentTagsEnum[
            tag
              .replace(/\s/g, '_')
              .trim()
              .toUpperCase() as keyof typeof TournamentTagsEnum
          ],
      )
      .filter((tag) => tag !== undefined)
      .sort(
        (a, b) => tagsOrder.indexOf(a) - tagsOrder.indexOf(b),
      ) as TournamentTagsEnum[];
  },
);

export const getTournamentSelectedTags = createSelector(
  getTournamentsState,
  (state: TournamentsState): string[] => state.filterByTags || [],
);

export const isAllTournamentsLoaded = createSelector(
  getTournamentsState,
  ({ cashTournaments, prizeTournaments }) => {
    const { items: cashItems = [], total: cashTotal = 0 } =
      cashTournaments || {};
    const { items: prizeItems = [], total: prizeTotal = 0 } =
      prizeTournaments || {};

    return {
      allCashLoaded: cashItems?.length <= cashTotal,
      allPrizeLoaded: prizeItems?.length >= prizeTotal,
    };
  },
);

export const getTournamentFilteringState = createSelector(
  getTournamentsState,
  (state: TournamentsState): boolean => state.filtering || false,
);

export const getTournamentLoadingState = createSelector(
  getTournamentsState,
  (state: TournamentsState): boolean => state.loading,
);

export const getSearchGameDetailsTerm = createSelector(
  getTournamentsState,
  (state: TournamentsState) => state.searchTerm || '',
);

export const getEsRangeValues = createSelector(
  getTournamentsState,
  (state: TournamentsState) => state.esRangeValues,
);

export const getGamesByEventsList = createSelector(
  getTournamentsState,
  (state: TournamentsState) => state.gamesByEventsList,
);

export const getPagination = createSelector(
  getTournamentsState,
  ({
    cashTournaments,
    prizeTournaments,
    pagesLoaded,
  }): AllTournamentsPagination => {
    return {
      cashTournaments: {
        total: cashTournaments?.total || 0,
        pageIndex: cashTournaments?.pageIndex || 0,
        pageSize: cashTournaments?.pageSize || 10,
        itemsLen: cashTournaments?.items?.length || 0,
        pagesLoaded: pagesLoaded?.cash || [],
      },
      prizeTournaments: {
        total: prizeTournaments?.total || 0,
        pageIndex: prizeTournaments?.pageIndex || 0,
        pageSize: prizeTournaments?.pageSize || 10,
        comingSoonTotal: prizeTournaments?.comingSoonTotal,
        leptTotal: prizeTournaments?.leptTotal,
        itemsLen: prizeTournaments?.items?.length || 0,
        pagesLoaded: pagesLoaded?.prize || [],
      },
      total: (prizeTournaments?.total || 0) + (cashTournaments?.total || 0),
    };
  },
);
export const getOrderedTournaments = createSelector(
  getAllTournaments,
  (tournaments: CasualTournamentEntity[]) => {
    const x = [...tournaments];
    x.sort((a, b) => {
      if (a.isComplete && !b.isComplete) {
        return 1;
      }

      if (!a.isComplete && b.isComplete) {
        return -1;
      }

      return 0;
    });

    return x;
  },
);
